import {
  ENDPOINTS,
  RerunPipelineContractRequestSchema,
  type RerunPipelineContractRequest,
  type RerunPipelineContractResponse,
} from '@clm/shared-types';

import { apiPost } from './client';
import { substitutePath } from './verifications';

/**
 * `rerun-pipeline-contract` — CLM-79 (completes R-70-1).
 *
 * Same recipe as `contract-tags.ts`: the path comes from the registry, the
 * request is `.parse`d before it leaves the browser (the schema is `.strict()`),
 * and the response is **parsed** rather than asserted so an off-contract answer
 * is a defect to report, not data to show.
 *
 * The reset is scoped server-side on `root_contract_key` (NFR-06) — an
 * out-of-scope key is a 404 here, never a 403. A 409 carries the reason in
 * `field_errors[0].message` (`retryable_pending`, `failed_after_ai`,
 * `not_failed`, `no_ledger_row`). The optional `reason` is logged by the API,
 * not stored (D-79-6). This call does **not** start a run.
 */

const ENDPOINT = ENDPOINTS['rerun-pipeline-contract'];

export async function rerunPipelineContract(
  request: RerunPipelineContractRequest,
  signal?: AbortSignal,
): Promise<RerunPipelineContractResponse> {
  const validated = RerunPipelineContractRequestSchema.parse(request);
  const path = substitutePath(ENDPOINT.path, { contract_key: validated.contract_key });
  const raw = await apiPost<unknown>(
    path,
    { ...(validated.reason === undefined ? {} : { reason: validated.reason }) },
    signal === undefined ? undefined : { signal },
  );
  return ENDPOINT.response.parse(raw);
}
